import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import axios from 'axios';
import { sendReport } from '../../functions/sendReport';
import { fetchThemeData } from '../../admin/themeSlice';
import { warnIfUnsupportedBrowser } from '../../utils/browserCheck';
import '../photobooth/photobooth.css';
import './avatar.css';

const API           = process.env.REACT_APP_PHOTOBOOTH_API_URL;
const FACESWAP_API  = process.env.REACT_APP_PHOTOBOOTH_FACESWAP_URL || API;
const S3            = process.env.REACT_APP_S3_PATH || '';
const NAME_MAX_LEN  = 30;
const GALLERY_LIMIT = 8;

// Must mirror the backend's EMOJI_SCORES (app.py) — display order + weights.
const REACTION_EMOJIS = ['❤️', '🔥', '😍', '👏', '👍'];

const extractReportId = (res) => {
  if (!res || typeof res !== 'object') return null;
  return res.reportId || res.id || res?.data?.reportId || res?.data?.id || null;
};

/** "captain_clarity.png" -> "Captain Clarity" */
const humanizeAvatarName = (filename = '') =>
  filename
    .replace(/\.[^/.]+$/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());

const parseObject = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

const PUBLIC_URL = process.env.PUBLIC_URL || '';

export default function Avatar() {
  const { user }            = useSelector(state => state.auth);
  const { data: themeData } = useSelector(state => state.theme);
  const navigate             = useNavigate();
  const dispatch              = useDispatch();

  const [managerName, setManagerName] = useState('');
  const [gender,      setGender]      = useState('');
  const [superhero,   setSuperhero]   = useState('');

  const [preview,      setPreview]      = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [cameraActive, setCamera]       = useState(false);
  const [saving,        setSaving]      = useState(false);
  // "Choose a ready animated avatar" — skips the photo step entirely and
  // ships the chosen avatar as-is (no face swap).
  const [useAnimatedAvatar, setUseAnimatedAvatar] = useState(false);
  // Tracks which of the 3 photo-picker cards produced the current preview,
  // purely for the selected-card highlight — doesn't affect what gets saved.
  const [photoSource, setPhotoSource] = useState(null); // 'upload' | 'camera' | null

  const videoRef      = useRef(null);
  const streamRef      = useRef(null);
  const fileInputRef    = useRef(null);

  const [gallery, setGallery] = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(0);
  const [floatEmojis, setFloatEmojis] = useState([]); // [{ id, emoji, x, y, dx, delay }]
  const floatIdRef      = useRef(0);
  const likeInFlightRef = useRef(new Set());

  const allAvatars  = themeData?.character || themeData?.avtar || [];
  const avatarMeta  = parseObject(themeData?.avatar_meta);

  const getAvatarLabel = (filename) =>
    avatarMeta[filename]?.characterName || humanizeAvatarName(filename);

  // Gender filter: avatars tagged M/F by the admin only show for the
  // matching selection; untagged avatars are gender-neutral and always
  // show; "other"/"prefer not to say"/no selection shows everything.
  const GENDER_TAG = { male: 'M', female: 'F' };
  const activeGenderTag = GENDER_TAG[gender];
  const avatars = activeGenderTag
    ? allAvatars.filter(av => {
        const tag = avatarMeta[av]?.gender;
        return !tag || tag === activeGenderTag;
      })
    : allAvatars;

  const selectedAvatarMeta = superhero ? avatarMeta[superhero] : null;

  // Admin's Manager tab toggle (themeUpdate.jsx) — when "csv" mode is on
  // and a name list was actually uploaded, pick from that list instead of
  // typing freely.
  const managerNameList = Array.isArray(themeData?.manager_names) ? themeData.manager_names : [];
  const managerNameListMode = themeData?.manager_names_mode === 'csv' && managerNameList.length > 0;

  // Drop the current selection if it's no longer in the list (e.g. the
  // admin just removed it) or if the mode switched away from list-picking.
  useEffect(() => {
    if (managerNameListMode && managerName && !managerNameList.includes(managerName)) {
      setManagerName('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managerNameListMode, themeData?.manager_names]);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  useEffect(() => {
    warnIfUnsupportedBrowser(Swal);
  }, []);

  // Drop the current superhero selection if a gender change filters it out.
  useEffect(() => {
    if (superhero && !avatars.includes(superhero)) {
      setSuperhero('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gender]);

  const galleryEnabled = themeData?.gallery_enabled !== false;

  useEffect(() => {
    if (user?.sessionId && galleryEnabled) fetchGallery(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.sessionId, galleryEnabled]);

  const authHeader = () => ({
    Authorization: `Bearer ${user?.sessionId}&${user?.organizationId}`,
  });

  // ── Camera ──────────────────────────────────────────────────────────────
  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      // getUserMedia is unavailable on insecure (non-HTTPS/non-localhost)
      // origins and on some older/embedded browsers — the generic camera
      // error below would be misleading here since no permission prompt
      // ever had a chance to appear.
      Swal.fire(
        'Camera Unavailable',
        'Camera access isn’t supported in this browser. Please use the upload option instead.',
        'error',
      );
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1920 }, height: { ideal: 1920 }, facingMode: 'user' },
      });
      streamRef.current = stream;
      setPreview(null);
      setSelectedFile(null);
      setPhotoSource(null);
      setUseAnimatedAvatar(false);
      setCamera(true);
    } catch {
      Swal.fire(
        'Camera Error',
        'Unable to access camera. Please allow camera access or use the upload option.',
        'error',
      );
    }
  };

  // Attach the stream once the <video> element actually exists (it only
  // mounts after cameraActive flips true) and explicitly call .play() —
  // Safari (especially iOS) can silently refuse to autoplay a stream that
  // was attached outside the original tap's user-activation window, which
  // is exactly what the previous setTimeout-based attach did. Left
  // unplayed, videoWidth/videoHeight stay 0 and the "captured" photo is a
  // blank image that fails face detection with a generic error later.
  useEffect(() => {
    if (!cameraActive || !videoRef.current || !streamRef.current) return;
    const video = videoRef.current;
    video.srcObject = streamRef.current;
    video.play().catch(() => {
      // Autoplay can still be rejected in rare cases (e.g. Low Power Mode) —
      // takeSnapshot() below guards against a video with no frames yet
      // rather than silently capturing a blank image.
    });
  }, [cameraActive]);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setCamera(false);
  };

  // Mobile front cameras happily deliver the full ideal 1920x1920 requested
  // below, while desktop webcams usually cap out much lower — an uncapped
  // snapshot at that resolution can push the base64 payload past prod's
  // upload size limit (Lambda Function URLs / API Gateway both cap
  // synchronous payloads in the single-digit MB range), which is why this
  // only broke on mobile. Downscale before encoding so the payload stays
  // small regardless of the source camera's resolution — plenty of detail
  // survives for face detection either way.
  const SNAPSHOT_MAX_SIDE = 1280;

  const captureViaCanvas = (video) => {
    const rawW = video.videoWidth;
    const rawH = video.videoHeight;

    // iOS Safari (and iOS Chrome/Edge, which are all WebKit under the hood)
    // can hand back a getUserMedia stream whose raw frame is landscape
    // while the phone is held upright — the live <video> preview still
    // looks correctly portrait because the browser rotates it for display,
    // but that rotation never touches the underlying pixel buffer. Drawing
    // the raw frame straight to canvas then produces a sideways photo that
    // still renders fine as a preview <img> (so nothing looks broken to the
    // user) but fails face detection on the backend — the "gets rotated"
    // symptom. Detect the mismatch by comparing the video's raw aspect
    // ratio against how it's actually being displayed on screen, and rotate
    // the capture to match what the user saw.
    const displayIsPortrait = video.clientHeight >= video.clientWidth;
    const rawIsPortrait     = rawH >= rawW;
    const needsRotation     = displayIsPortrait !== rawIsPortrait;

    const scale  = Math.min(1, SNAPSHOT_MAX_SIDE / Math.max(rawW, rawH));
    const scaledW = Math.round(rawW * scale);
    const scaledH = Math.round(rawH * scale);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (needsRotation) {
      canvas.width  = scaledH;
      canvas.height = scaledW;
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(90 * Math.PI / 180);
      ctx.drawImage(video, -scaledW / 2, -scaledH / 2, scaledW, scaledH);
    } else {
      canvas.width  = scaledW;
      canvas.height = scaledH;
      ctx.drawImage(video, 0, 0, scaledW, scaledH);
    }

    return canvas.toDataURL('image/jpeg', 0.85);
  };

  const takeSnapshot = async () => {
    const video = videoRef.current;
    if (!video) return;
    if (!video.videoWidth || !video.videoHeight) {
      // The stream hasn't actually started rendering frames yet (seen on
      // iOS Safari when autoplay gets delayed) — capturing now would
      // silently produce a blank image that fails later with a confusing
      // "something went wrong" error, so bail out with a clear message and
      // let the user try again once the live preview is actually visible.
      Swal.fire('Camera not ready', 'Please wait a moment for the camera preview to load, then try again.', 'warning');
      return;
    }

    // Prefer the browser's own photo-capture pipeline (ImageCapture) over
    // drawing the raw video frame to a canvas. iOS Safari's getUserMedia
    // stream can carry orientation metadata that the live <video> preview
    // respects (so it looks correctly oriented to the user) but that a raw
    // canvas drawImage() ignores — producing a sideways photo that still
    // renders fine as a preview image but then fails face detection on the
    // backend. ImageCapture.takePhoto() goes through the same pipeline the
    // native camera app uses, so it comes out correctly oriented.
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (track && typeof window.ImageCapture === 'function') {
      try {
        const capture = new window.ImageCapture(track);
        const blob = await capture.takePhoto();
        const dataUrl = await downscaleImageFile(blob);
        setPreview(dataUrl);
        setSelectedFile({ type: 'base64', data: dataUrl });
        setPhotoSource('camera');
        stopCamera();
        return;
      } catch (err) {
        console.warn('ImageCapture failed, falling back to canvas capture', err);
      }
    }

    const dataUrl = captureViaCanvas(video);
    setPreview(dataUrl);
    setSelectedFile({ type: 'base64', data: dataUrl });
    setPhotoSource('camera');
    stopCamera();
  };

  // ── File input ──────────────────────────────────────────────────────────
  // Same payload-size concern as the live camera snapshot above — a phone's
  // native camera picked through this file dialog can produce a multi-MB
  // photo, so downscale it the same way before sending instead of shipping
  // the raw file.
  const downscaleImageFile = (file) => new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, SNAPSHOT_MAX_SIDE / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width  = Math.round(img.width  * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(objectUrl);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not read image'));
    };
    img.src = objectUrl;
  });

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['jpg', 'jpeg', 'png'].includes(ext)) {
      Swal.fire('Invalid file', 'Please select a JPG or PNG image.', 'error');
      return;
    }
    setUseAnimatedAvatar(false);
    try {
      const dataUrl = await downscaleImageFile(file);
      setSelectedFile({ type: 'base64', data: dataUrl });
      setPreview(dataUrl);
    } catch {
      // Fall back to the raw file if it couldn't be decoded/downscaled for
      // any reason — better to attempt the upload as-is than to block it.
      setSelectedFile({ type: 'file', data: file });
      setPreview(URL.createObjectURL(file));
    }
    setPhotoSource('upload');
  };

  const clearPhoto = () => {
    setPreview(null);
    setSelectedFile(null);
    setPhotoSource(null);
  };

  const chooseAnimatedAvatar = () => {
    stopCamera();
    setPreview(null);
    setSelectedFile(null);
    setPhotoSource(null);
    setUseAnimatedAvatar(true);
  };

  const backToPhotoUpload = () => {
    setUseAnimatedAvatar(false);
  };

  // ── Save & Continue ────────────────────────────────────────────────────
  const handleSave = async () => {
    const trimmedName = managerName.trim();
    if (!trimmedName) {
      Swal.fire('Name required', "Please enter your manager's name.", 'warning');
      return;
    }
    if (!gender) {
      Swal.fire('Gender required', 'Please select a gender.', 'warning');
      return;
    }
    if (!useAnimatedAvatar && !selectedFile) {
      Swal.fire('No photo', "Please upload or take a photo of your manager (not yourself) first.", 'warning');
      return;
    }
    if (!superhero) {
      Swal.fire('Superhero required', 'Please choose a superhero avatar.', 'warning');
      return;
    }

    setSaving(true);
    try {
      const form = new FormData();
      form.append('userId', user.userId);
      form.append('name',   trimmedName);
      form.append('email',  user.email || '');
      form.append('gender', gender);
      if (useAnimatedAvatar) {
        form.append('skipPhoto', 'true');
      } else if (selectedFile.type === 'base64') {
        form.append('image', selectedFile.data);
      } else {
        form.append('file', selectedFile.data);
      }
      await axios.post(`${API}/api/photobooth/upload`, form, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });

      try {
        const report = await sendReport({
          role: user.role,
          token: user.token,
          points: 0,
          time: "00:00",
          gameId: user.gameId,
          name: trimmedName,
          sessionId: user.sessionId,
          userId: user.userId,
          organizationId: user.organizationId,
          ans: "",
        });
        const likesid = extractReportId(report);
        if (likesid) {
          await axios.post(
            `${API}/api/photobooth/likeid`,
            { userId: user.userId, likesid },
            { headers: { ...authHeader(), 'Content-Type': 'application/json' } },
          );
        }
      } catch (reportErr) {
        console.error('sendReport (create) failed', reportErr);
      }

      Swal.fire({
        title:              'Processing…',
        text:               'Your superhero avatar is being created. This may take about 30 seconds.',
        showConfirmButton:  false,
        allowOutsideClick:  false,
        didOpen:            () => Swal.showLoading(),
      });

      await axios.post(
        `${FACESWAP_API}/api/photobooth/faceswap`,
        {
          userId:       user.userId,
          avatarName:   superhero || '',
          managerName:  trimmedName,
          superpower:   selectedAvatarMeta?.description || '',
          superheroName: superhero ? getAvatarLabel(superhero) : '',
          accentColor:  themeData?.button_color || '',
          skipFaceswap: useAnimatedAvatar,
        },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' }, timeout: 180_000 },
      );

      Swal.close();
      navigate('/photobooth/final');
    } catch (err) {
      // err.response is only set once a response actually comes back — a
      // request that never completed (network drop, CORS, timeout) leaves
      // it undefined, which used to fall through to the same unhelpful
      // "Please try again." for every failure mode. Distinguishing them
      // makes it possible to tell a real backend rejection (e.g. "no face
      // detected") apart from a connectivity problem when debugging reports
      // from a device we can't reproduce on directly.
      const message = err.response?.data?.error
        || (err.request ? 'No response from server — please check your connection and try again.' : err.message)
        || 'Please try again.';
      Swal.fire('Something went wrong', message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Gallery ─────────────────────────────────────────────────────────────
  const fetchGallery = async (p) => {
    try {
      const res = await axios.post(
        `${API}/api/photobooth/gallery`,
        { userId: user.userId, page: p, limit: GALLERY_LIMIT },
        { headers: authHeader() },
      );
      setGallery(res.data.items || []);
      setTotal(res.data.total  || 0);
      setPage(p);
    } catch (err) {
      console.error('Gallery fetch failed', err);
    }
  };

  const spawnBurst = (emoji) => {
    const ids = [];
    const particles = Array.from({ length: 8 }).map(() => {
      const id = floatIdRef.current++;
      ids.push(id);
      return {
        id,
        emoji,
        x: Math.random() * window.innerWidth,
        y: window.innerHeight,
        dx: (Math.random() - 0.5) * 120,
        delay: Math.random() * 300,
      };
    });
    setFloatEmojis(prev => [...prev, ...particles]);
    setTimeout(() => {
      setFloatEmojis(prev => prev.filter(p => !ids.includes(p.id)));
    }, 2600);
  };

  const handleLike = async (post, emoji) => {
    if (post.liked) {
      Swal.fire('', 'You already reacted to this post!', 'info').then(() => {
        window.location.reload();
      });
      return;
    }
    if (likeInFlightRef.current.has(post.id)) return;
    spawnBurst(emoji);
    likeInFlightRef.current.add(post.id);
    try {
      const res = await axios.post(
        `${API}/api/photobooth/like`,
        { userId: user.userId, postId: post.id, emoji },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } },
      );
      if (res.data.success) {
        const reactedEmoji = res.data.emoji || emoji;
        const reactorName  = user.name || user.email || 'You';
        setGallery(prev =>
          prev.map(p => {
            if (p.id !== post.id) return p;
            const prevCounts = p.reactionCounts || {};
            const prevUsers  = p.reactionUsers  || {};
            return {
              ...p,
              liked:      true,
              myReaction: reactedEmoji,
              likes:      res.data.likes,
              reactionCounts: {
                ...prevCounts,
                [reactedEmoji]: (prevCounts[reactedEmoji] || 0) + 1,
              },
              reactionUsers: {
                ...prevUsers,
                [reactedEmoji]: [...(prevUsers[reactedEmoji] || []), reactorName],
              },
            };
          }),
        );

        if (post.likesid) {
          try {
            await sendReport({
              role: user.role,
              token: user.token,
              reportId: post.likesid,
              points: res.data.likes,
              time: "00:00",
            });
          } catch (reportErr) {
            console.error('sendReport (like point) failed', reportErr);
          }
        }
      }
    } catch {
    } finally {
      likeInFlightRef.current.delete(post.id);
    }
  };

  const handleShowRules = () => {
    const rules = Array.isArray(themeData?.rules) ? themeData.rules : [];
    const rulesHtml = rules.length
      ? `<ul class="av-rules-popup-list">${rules
          .map(rule => `<li>${rule}</li>`)
          .join('')}</ul>`
      : '<p>No rules available for this theme.</p>';

    const rulesBgUrl = themeData?.background_desk ? `${S3}${themeData.background_desk}` : '';

    Swal.fire({
      title: themeData?.main_title || 'Rules',
      html: rulesHtml,
      confirmButtonText: 'Got it',
      confirmButtonColor: themeData?.button_color || '#2f6fed',
      customClass: { popup: 'av-rules-popup' },
      didOpen: (popupEl) => {
        if (rulesBgUrl) popupEl.style.setProperty('--rules-bg-url', `url("${rulesBgUrl}")`);
        popupEl.style.setProperty('--rules-text-color', themeData?.text_color || '#fff');
      },
    });
  };

  if (!themeData) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader"></div>
      </div>
    );
  }

  const isMobile = window.innerWidth <= 768;
  const bgImg    = isMobile ? themeData.background_mob : themeData.background_desk;
  const bgStyle  = bgImg
    ? {
        backgroundImage:      `url("${S3}${bgImg}")`,
        backgroundSize:       '100% 100%',
        backgroundRepeat:     'no-repeat',
        backgroundAttachment: 'fixed',
      }
    : {};

  const btnStyle = {
    background: themeData?.button_background
      ? themeData.button_background
      : `linear-gradient(180deg, ${themeData?.button_color || '#fb9905'}, ${themeData?.button_color || '#f97316'})`,
    color: themeData?.button_text_color ? themeData.button_text_color : '#fff',
  };

  const totalPages = Math.ceil(total / GALLERY_LIMIT) || 0;

  return (
    <div className="av-page" style={bgStyle}>
      <div className="av-wrap">
        {themeData?.logo ? (
          <img
            src={`${S3}${themeData.logo}`}
            alt={themeData?.main_title || 'Logo'}
            className="av-logo-img"
          />
        ) : themeData?.main_title ? (
          <div className="av-logo-title" style={{ color: themeData?.text_color }}>
            {themeData.main_title}
          </div>
        ) : null}

        <div className="av-ribbon-wrap">
          <img src={`${PUBLIC_URL}/img/tell_us_trimmed.png`} alt="Tell us about your manager" className="av-ribbon-img" />
          <button
            type="button"
            className="av-help-btn"
            onClick={handleShowRules}
            aria-label="View rules"
            title="View rules"
          >
            ?
          </button>
        </div>

        <div className="av-card">
          <div className="av-grid">
            {/* ── Left column ───────────────────────────────── */}
            <div className="av-col">
              <div className="av-field">
                <label className="av-label" style={{ color: themeData?.text_color }}>{themeData?.manager_name_label || "Manager's Name"}</label>
                {managerNameListMode ? (
                  <select
                    className="av-input av-select"
                    value={managerName}
                    onChange={e => setManagerName(e.target.value)}
                  >
                    <option value="" disabled>Select manager</option>
                    {managerNameList.map((n, i) => (
                      <option key={i} value={n}>{n}</option>
                    ))}
                  </select>
                ) : (
                  <>
                    <input
                      type="text"
                      className="av-input"
                      placeholder="Enter your name"
                      value={managerName}
                      maxLength={NAME_MAX_LEN}
                      onChange={e => setManagerName(e.target.value.slice(0, NAME_MAX_LEN))}
                    />
                    <div className="av-hint">{managerName.length}/{NAME_MAX_LEN}</div>
                  </>
                )}
              </div>

              <div className="av-field">
                <label className="av-label" style={{ color: themeData?.text_color }}>Gender</label>
                <select
                  className="av-input av-select"
                  value={gender}
                  onChange={e => setGender(e.target.value)}
                >
                  <option value="" disabled>Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>

              <div className="av-field">
                <label className="av-label" style={{ color: themeData?.text_color }}>Photo</label>

                <div className="av-photo-picker-row">
                  <button
                    type="button"
                    className={`av-photo-picker-card${photoSource === 'upload' && preview && !useAnimatedAvatar ? ' av-photo-picker-card-selected' : ''}`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {photoSource === 'upload' && preview && !useAnimatedAvatar && (
                      <span className="av-photo-picker-check">✓</span>
                    )}
                    <span className="av-photo-picker-icon av-photo-picker-icon-upload" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                        <path
                          d="M7 18a4.5 4.5 0 0 1-.4-8.98A5.5 5.5 0 0 1 17.3 8.1 4 4 0 0 1 17 16H7Z"
                          stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"
                        />
                        <path d="M12 11v6M9.5 13.5 12 11l2.5 2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <span className="av-photo-picker-title">Upload Photo</span>
                    <span className="av-photo-picker-sub">Upload from device</span>
                  </button>

                  <button
                    type="button"
                    className={`av-photo-picker-card${cameraActive || (photoSource === 'camera' && preview && !useAnimatedAvatar) ? ' av-photo-picker-card-selected' : ''}`}
                    onClick={startCamera}
                  >
                    {(cameraActive || (photoSource === 'camera' && preview && !useAnimatedAvatar)) && (
                      <span className="av-photo-picker-check">✓</span>
                    )}
                    <span className="av-photo-picker-icon av-photo-picker-icon-camera" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                        <path d="M4 8.5A1.5 1.5 0 0 1 5.5 7h2l1-1.5h7l1 1.5h2A1.5 1.5 0 0 1 20 8.5v9A1.5 1.5 0 0 1 18.5 19h-13A1.5 1.5 0 0 1 4 17.5v-9Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                        <circle cx="12" cy="13" r="3.2" stroke="currentColor" strokeWidth="1.6" />
                      </svg>
                    </span>
                    <span className="av-photo-picker-title">Take a Selfie</span>
                    <span className="av-photo-picker-sub">Use your camera</span>
                  </button>

                  <button
                    type="button"
                    className={`av-photo-picker-card${useAnimatedAvatar ? ' av-photo-picker-card-selected' : ''}`}
                    onClick={chooseAnimatedAvatar}
                  >
                    {useAnimatedAvatar && <span className="av-photo-picker-check">✓</span>}
                    <span className="av-photo-picker-icon av-photo-picker-icon-avatar" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                        <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
                        <circle cx="9" cy="10.5" r="1" fill="currentColor" />
                        <circle cx="15" cy="10.5" r="1" fill="currentColor" />
                        <path d="M8.5 14.5c1 1 2.2 1.5 3.5 1.5s2.5-.5 3.5-1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      </svg>
                    </span>
                    <span className="av-photo-picker-title">Choose Avatar</span>
                    <span className="av-photo-picker-sub">Pick an animated avatar</span>
                  </button>
                </div>

                <div className="av-hint av-photo-picker-hint">
                  {themeData?.upload_instruction_text
                    || 'Upload or take a photo of your manager — not yourself — or skip the photo entirely with a ready animated avatar.'}
                </div>

                <div className="av-photo-row">
                  {useAnimatedAvatar ? (
                    <div className="av-photo-preview">
                      <div className="av-animated-avatar-note">
                        {themeData?.animated_avatar_text
                          || "Using a ready animated avatar — no photo needed. Your manager's name and description will be added to the avatar as-is."}
                      </div>
                      <div className="av-photo-actions">
                        <button className="av-btn-secondary" onClick={backToPhotoUpload}>
                          Upload a photo instead
                        </button>
                      </div>
                    </div>
                  ) : cameraActive ? (
                    <div className="av-camera-live">
                      <video ref={videoRef} autoPlay playsInline muted className="av-video" />
                      <div className="av-photo-actions">
                        <button className="av-btn-secondary" onClick={stopCamera}>Cancel</button>
                        <button className="av-btn-primary" style={btnStyle} onClick={takeSnapshot}>
                          📸 Capture
                        </button>
                      </div>
                    </div>
                  ) : preview ? (
                    <div className="av-photo-preview">
                      <img src={preview} alt="Selfie preview" className="av-photo-preview-img" />
                      <div className="av-photo-actions">
                        <button className="av-btn-secondary" onClick={clearPhoto}>Retake</button>
                      </div>
                    </div>
                  ) : null}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            </div>

            {/* ── Right column ──────────────────────────────── */}
            <div className="av-col av-col-right">
              <div className="av-field">
                <label className="av-label" style={{ color: themeData?.text_color }}>{themeData?.superhero_name_label || "Superhero Name"}</label>
                <div className="av-hint av-hint-top">{themeData?.catchy_phrase_text || 'Name with a catchy phrase'}</div>
                <select
                  className="av-input av-select"
                  value={superhero}
                  onChange={e => setSuperhero(e.target.value)}
                >
                  <option value="">e.g. Captain Clarity</option>
                  {avatars.map((av, i) => (
                    <option key={i} value={av}>{getAvatarLabel(av)}</option>
                  ))}
                </select>

                {superhero && (
                  <>
                    <div className="av-superhero-preview">
                      <img
                        src={`${S3}avatar/${superhero}`}
                        alt={getAvatarLabel(superhero)}
                        onError={e => { e.target.style.opacity = '0.2'; }}
                      />
                    </div>
                    {selectedAvatarMeta?.description && (
                      <div className="av-superpower-box">
                        <div className="av-superpower-label">Superpower</div>
                        <div className="av-superpower-text">{selectedAvatarMeta.description}</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="av-save-row">
            <button
              className="av-save-btn"
              style={btnStyle}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save & Continue'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Gallery ───────────────────────────────────────────────── */}
      {galleryEnabled && gallery.length > 0 && (
        <div className="pb-gallery-section av-gallery-section">
          <h3 className="pb-gallery-title" style={{ color: themeData?.text_color }}>Gallery</h3>

          <div className="pb-grid">
            {gallery.map(item => {
              const isBlankName = !item.name || item.name.trim().toLowerCase() === "null null";
              const displayName = isBlankName ? item.email : item.name;
              return (
                <div key={item.id} className="pb-pin">
                  <div className="pb-pin-img-wrap">
                    <img src={item.imageUrl} alt={displayName} className="pb-pin-img" />
                  </div>
                  <div className="pb-pin-footer">
                    <div className="pb-reaction-row">
                      {REACTION_EMOJIS.map(emoji => {
                        const isMine = item.liked && item.myReaction === emoji;
                        const count  = item.reactionCounts?.[emoji] || 0;
                        const names  = item.reactionUsers?.[emoji] || [];
                        const tooltip = count > 0
                          ? `${emoji} (${count})\n${names.join('\n')}`
                          : emoji;
                        return (
                          <button
                            key={emoji}
                            className={`pb-reaction-btn ${isMine ? 'picked' : ''}`}
                            disabled={item.liked}
                            onClick={() => handleLike(item, emoji)}
                            title={tooltip}
                          >
                            <span className="pb-reaction-emoji">{emoji}</span>
                            {count > 0 && <span className="pb-reaction-count">{count}</span>}
                          </button>
                        );
                      })}
                    </div>
                    <span className="pb-pin-name">{displayName}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="pb-pagination">
              <button
                disabled={page === 0}
                onClick={() => fetchGallery(page - 1)}
                style={btnStyle}
              >
                ← Prev
              </button>
              <span>{page + 1} / {totalPages}</span>
              <button
                disabled={page >= totalPages - 1}
                onClick={() => fetchGallery(page + 1)}
                style={btnStyle}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Floating reaction emojis ──────────────────────────────── */}
      <div className="pb-emoji-layer">
        {floatEmojis.map(f => (
          <span
            key={f.id}
            className="pb-float-emoji"
            style={{
              left: `${f.x}px`,
              top: `${f.y}px`,
              '--dx': `${f.dx}px`,
              animationDelay: `${f.delay}ms`,
            }}
          >
            {f.emoji}
          </span>
        ))}
      </div>
    </div>
  );
}
